import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { ThemeMode } from '@/helpers/enums';
import { useToggle } from '@/hooks/common';
import { useCommonStore, useDepositStore } from '@/store';

import { ScCurrencyItem } from './styled';
import { Spinner } from '@/styles/common';

interface CurrencyItemProps {
  currencyCode: string;
  label: string;
  icon?: string;
  isActive?: boolean;
  disableButton?: boolean;
  noAction?: boolean;
}

export const CurrencyItem: FC<CurrencyItemProps> = ({
  currencyCode,
  label,
  icon,
  isActive,
  disableButton,
  noAction,
}) => {
  const { t } = useTranslation('common');
  const { images, greenColor, whiteColor, gray1Color } = useTheme();

  const setOriginalAmount = useDepositStore.use.setOriginalAmount();
  const {
    deleteUserWallet,
    changeUserWallet,
    currentWallet,
    pendingWallets,
    isMobileDevice,
    extendedUserWallets,
  } = useCommonStore(
    useShallow((state) => ({
      deleteUserWallet: state.deleteUserWallet,
      changeUserWallet: state.changeUserWallet,
      currentWallet: state.currentWallet,
      pendingWallets: state.pendingWallets,
      isMobileDevice: state.isMobileDevice,
      extendedUserWallets: state.extendedUserWallets,
    }))
  );

  const isCurrentCurrency = useMemo(
    () => currentWallet?.currency === currencyCode,
    [currentWallet?.currency, currencyCode]
  );

  const [pending, togglePending] = useToggle();

  const handleItemClick = useCallback(async () => {
    if (isActive && !isCurrentCurrency) {
      await changeUserWallet(currencyCode);
      setOriginalAmount('');
    }
  }, [
    currencyCode,
    isActive,
    changeUserWallet,
    isCurrentCurrency,
    setOriginalAmount,
  ]);

  const handleActionClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOriginalAmount('');

      if (isActive) {
        if (currentWallet?.currency === currencyCode) {
          const { currency } = extendedUserWallets.find(
            ({ currency }) => currency !== currencyCode
          )!;
          await deleteUserWallet(currencyCode, currency);
          setOriginalAmount('');
        } else {
          deleteUserWallet(currencyCode);
        }
      } else {
        await changeUserWallet(currencyCode);
        setOriginalAmount('');
      }

      togglePending();
    },
    [
      currencyCode,
      isActive,
      currentWallet,
      extendedUserWallets,
      deleteUserWallet,
      changeUserWallet,
      togglePending,
    ]
  );

  const buttonParams = useMemo(
    () =>
      !isMobileDevice || isCurrentCurrency
        ? { blackTransparent: true }
        : { whiteTransparent: true },
    [isMobileDevice, isCurrentCurrency]
  );

  return (
    <ScCurrencyItem
      isCurrent={isCurrentCurrency}
      noAction={!!noAction}
      isActive={isActive}
      onClick={handleItemClick}
      mode={isMobileDevice ? ThemeMode.Dark : ThemeMode.Light}
    >
      <div className="currency-info">
        {icon && (
          <ImageComponent src={icon} alt="currency" width={24} height={24} />
        )}
        <p className="symbol">{label}</p>
      </div>

      {(isCurrentCurrency || !noAction) && (
        <div className="actions">
          {isCurrentCurrency &&
            (pendingWallets && noAction ? (
              <Spinner
                size={20}
                margin="0 4px"
                color={greenColor}
                background={isMobileDevice ? whiteColor : gray1Color}
              />
            ) : (
              <images.tickCircleIcon.component />
            ))}

          {!noAction && (
            <Button
              {...buttonParams}
              isLoading={pending}
              onClick={handleActionClick}
              disabled={disableButton}
              xSmall
              autoPaddingHorizontal
              paddingX={11}
            >
              {isActive ? t('button.remove') : t('button.add')}
            </Button>
          )}
        </div>
      )}
    </ScCurrencyItem>
  );
};
